import { useEffect, useState } from "react";

/**
 * Returns the number of seconds remaining until the next refetch.
 * Resets automatically whenever `dataUpdatedAt` changes (i.e. after every refetch).
 *
 * @param dataUpdatedAt - timestamp (ms) of the last successful fetch, from useQuery
 * @param intervalMs    - refetch interval in milliseconds (must match the query's refetchInterval)
 */
export const useRefetchCountdown = (dataUpdatedAt: number, intervalMs: number): number => {
  const intervalSec = Math.round(intervalMs / 1000);

  const getSecondsLeft = () => {
    if (!dataUpdatedAt) return intervalSec;
    const elapsed = Math.floor((Date.now() - dataUpdatedAt) / 1000);
    return Math.max(0, intervalSec - elapsed);
  };

  const [secondsLeft, setSecondsLeft] = useState<number>(getSecondsLeft);

  // Reset counter whenever a new fetch lands
  useEffect(() => {
    setSecondsLeft(getSecondsLeft());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  // Tick every second
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft(getSecondsLeft());
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, intervalMs]);

  return secondsLeft;
};
